import { useMemo, useState, useEffect, Suspense } from "react";
import parse from "html-react-parser";
import * as THREE from "three";
import { Canvas, applyProps } from "@react-three/fiber";
import {
	useGLTF,
	BakeShadows,
	Environment,
	ContactShadows,
	OrbitControls,
	Html,
	Lightformer,
	Float
} from "@react-three/drei";
import { LayerMaterial, Base, Depth } from 'lamina'
import dynamic from "next/dynamic";
import { selectImageForDevice } from "../../../../utils";
import { SiteContext } from "../../../Contexts/SiteContext";
import TagManager from 'react-gtm-module';
import { getBlockId } from "../../../../utils/forms/form.fields.utils";
import { RegularIcon } from "../../Icons/regular";

const LazyLoad = dynamic(import("../../Image/LazyLoad"));

const VirtualTour = props => {
	const { block, block: { data } } = props;
	const { model, hdri } = data;

	const [colours, setColours] = useState(null);
	const [activeColour, setActiveColour] = useState(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [tourActive, setTourActive] = useState(false);
	const [rotateModel, setRotateModel] = useState(true)
	const [checked, setChecked] = useState(false);

	useEffect(() => {
		setColours(block.data.colours)
		setActiveColour(block.data.colours[0])
		setRotateModel(data.autoRotate)
		if (data.hasOwnProperty('autoload')) {
			setTourActive(data.autoload)
		}

	}, []);

	const createDataLayer = async (e, type, data) => {

		let tagManagerArgs;

		if (type == "colour") {
			tagManagerArgs = {
				dataLayerName: 'PageDataLayer',
				dataLayer: {
					event: "colourSelect",
					product: "HR-V",
					colorType: data.type,
					color: data.title
				}
			}
		}

		if (type == "click") {
			tagManagerArgs = {
				dataLayerName: 'PageDataLayer',
				dataLayer: {
					event: "initExperience",
					product: "HR-V",
				}
			}
		}

		if (typeof window !== 'undefined' && tagManagerArgs !== undefined) {
			await TagManager.dataLayer(tagManagerArgs);
		}
	}

	const Loader = () => {
		return (
			<Html center>
				<div className={"vt-loader"}>
					<div className="loader center-align mt32px">
						<RegularIcon
							icon={{ icon: "spinner-third" }}
							spin
							size="6x"
						/>
						<div className="mt32px">
							<span>Loading Model</span>
						</div>
					</div>
				</div>
			</Html>
		);
	};

	const buttons = () => {
		return colours.map((colour, i) => {
			return (
				<div
					className={`colour ${activeIndex === i ? "active" : ""
						}`}
					key={`colour-${i}`}
					onClick={(e) => {
						setActiveColour(colour);
						setActiveIndex(i);
						createDataLayer(e, 'colour', colour)
					}}
				>
					<div
						className="left"
						style={{
							backgroundColor: colour.buttonColour1
						}}
					></div>
					<div
						className="right"
						style={{
							backgroundColor: colour.buttonColour2
						}}
					></div>
				</div>
			);
		});
	};



	const ModelSwitch = () => {
		return (
			<div className="colours">
				<label>
					<span className="switch-label" >Low</span>
					<span className="switch-wrapper" style={checked ? { border: "2px solid grey" } : {}} >
						<input
							type="checkbox"
							checked={checked}
							disabled={checked}
							onChange={e => {
								// console.log(e.target.checked);
								setChecked(e.target.checked)
								// setActiveModel(checked ? model.modelHigh.url : model.modelLow.url)
							}}
						/>
						<span className={`switch-${checked}`} >
							{/* <span className="switch-handle" /> */}
						</span>
					</span>
					<span className="switch-label">High</span>
				</label>
			</div>
		);
	}

	// const Striplight = (props) => {
	// 	return (
	// 		<mesh {...props}>
	// 			<boxGeometry />
	// 			<meshBasicMaterial color="white" />
	// 		</mesh>
	// 	)
	// }


	const ThreeModel = (props) => {

		const { scene, nodes, materials } = useGLTF(props.modelType);

		const windowglass = new THREE.MeshPhysicalMaterial({
			color: "#000000",
			opacity: 0.3,
			roughness: 0,
			metalness: 0,
			transmission: 0.3,
			thickness: 0.05, // Add refraction!
		});

		const clearglass = new THREE.MeshPhysicalMaterial({
			color: "#666",
			opacity: 0.2,
			// metalness: 0.1,
			roughness: 0,
			transmission: 0.7,
			roughness: 0,
			thickness: 0.01, // Add refraction!
		});

		const redglass = new THREE.MeshPhysicalMaterial({
			color: "#b50202",
			opacity: 0.2,
			// specularIntensity: 0.1,
			// specularColor: "#1f1f1f",
			roughness: 0,
			metalness: 0,
			transmission: 0.7,
			// thickness: 0.05, // Add refraction!
		});

		const mirror = new THREE.MeshPhysicalMaterial({
			color: "#666",
			roughness: 0,
			metalness: 0.8,
		});

		const rims = new THREE.MeshPhysicalMaterial({
			color: "#333",
			roughness: 0,
			metalness: 0.9,
		});

		// console.log(activeColour.sheenAmount);

		const carpaint = new THREE.MeshPhysicalMaterial({
			clearcoat: activeColour.clearcoat,
			clearcoatRoughness: activeColour.clearcoatRoughness,
			metalness: activeColour.metalness,
			roughness: activeColour.roughness,
			sheen: activeColour.sheenAmount,
			sheenColor: activeColour.sheenColour,
			reflectivity: 0.9,
			color: activeColour.paintColour

			// normalMap: new THREE.CanvasTexture(
			// 	new FlakesTexture(),
			// 	THREE.UVMapping,
			// 	THREE.RepeatWrapping,
			// 	// THREE.RepeatWrapping
			// ),
			// "normalMap-repeat": [40, 40],
			// normalScale: [0.001, 0.001],
		});


		useMemo(() => {
			Object.values(nodes).forEach((node) => {
				if (node.isMesh) {
					node.receiveShadow = node.castShadow = true;
					// if (node.name === "HR-V_15") node.material = windowglass;
					if (node.name === "HR-V_4") node.material = mirror;
					if (node.name === "HR-V_10") node.material = mirror;
					if (node.name === "HR-V_9") node.material = clearglass;
					if (node.name === "HR-V_11") node.material = redglass;
					if (node.name === "HR-V_15") node.material = windowglass;
					if (node.name === "HR-V_1") node.material = carpaint;
					if (node.name === "HR-V_13") node.material = rims;

					// if (node.material.name === "windowglass") node.material = windowglass;
					// if (node.material.name === "clearglass") node.material = clearglass;
					// if (node.material.name === "mirror") node.material = mirror;
					// if (node.material.name === "brakedisk") node.material = mirror;
					// if (node.material.name === "redglass") node.material = redglass;

					// console.log(node.name, node.material.name);
				}
			});
			// console.log(materials);
			applyProps(materials.chrome, {
				metalness: 1,
				roughness: 0.2,
				color: "#e6e6e6",
			});
			applyProps(materials.underside, {
				metalness: 0,
				roughness: 0.6,
				color: "#000",
			});
			// applyProps(materials.rim_second, {
			// 	metalness: 0.1,
			// 	roughness: 0.1,
			// 	color: "#333",
			// 	envMapIntensity: 2
			// });
			applyProps(materials.tire, {
				color: '#555', metalness: 0.7, roughness: 0.2, envMapIntensity: 1
			});
			applyProps(materials.black, {
				color: "#333", metalness: 0.4, roughness: 0.2, envMapIntensity: 1.5
			});
			applyProps(materials.mattemetal, {
				metalness: 1,
				roughness: 0.5,
				color: "#e6e6e6",
			});
			applyProps(materials.windowglass, { metalness: 0, color: "#292929" });
			applyProps(materials.brakelight, { metalness: 0, color: "#870900" });
			// applyProps(materials.carpaint, {
			// 	roughness: activeColour.roughness, 
			// 	metalness: activeColour.metalness, 
			// 	color: activeColour.paintColour, 
			// 	envMapIntensity: activeColour.envIntensity
			// });
		}, [nodes, materials]);
		return <primitive object={scene} {...props} />;
	};

	return (
		<>
			{data &&
				<SiteContext.Consumer>
					{({ deviceType }) => (
						<div className="vtParent customContainer mb64px" id={getBlockId(block)}>
							{/* {deviceType == 'desktop' && */}
							<>
								<h1 className="cbTitle">{data.title}</h1>
								<div className="col s12 cbDescription mb32px">
									{parse(block.data.description)}
								</div>
								<p>{rotateModel}</p>
								{!tourActive &&
									<div
										onClick={() => {
											setTourActive(true);
										}}
									>
										<LazyLoad
											image={selectImageForDevice(
												deviceType,
												data
											)}
											alt={block.data.title}
											title={block.data.title}
											containVideoOverlay={true}
										/>
									</div>

								}

								{tourActive &&
									<>
										<div className={"sceneWrapper"}>
											<Canvas shadows dpr={[1, 2]} camera={{ position: [-5, 0, 5], fov: 22 }}

												onClick={(e) => {
													createDataLayer(e, 'click')
												}}
											>
												<spotLight position={[0, 15, 0]} angle={0.3} penumbra={1} castShadow intensity={2} shadow-bias={-0.0001} />
												{/* <ambientLight intensity={0.2} /> */}

												<Suspense fallback={<Loader />}>
													<ContactShadows resolution={1024} frames={1} position={[0, -0.4, 0]} scale={10} blur={3} opacity={0.5} far={10} />

													{!checked ?
														<ThreeModel
															modelType={model.modelLow.url}
															scale={1} position={[0, -0.4, 0]} rotation={[0, Math.PI / 4, 0]}
														/>
														:
														<ThreeModel
															modelType={model.modelHigh.url}
															scale={1} position={[0, -0.4, 0]} rotation={[0, Math.PI / 4, 0]}
														/>
													}


													<Environment
														files={hdri.url}
														frames={Infinity}
														resolution={256}
													>
														{/* Ceiling */}
														{/* <Lightformer intensity={0.75} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={[10, 10, 1]} /> */}
														{/* <MovingSpots /> */}
														{/* Sides */}
														<Lightformer intensity={1} rotation-y={Math.PI / 2} position={[-5, 2, 0]} scale={[20, 2]} />
														<Lightformer intensity={1} rotation-y={Math.PI / -2} position={[5, 2, 0]} scale={[20, 2]} />
														{/* <Lightformer intensity={2} rotation-y={Math.PI / 2} position={[-5, -1, -1]} scale={[20, 0.5, 1]} /> */}
														{/* <Lightformer rotation-y={-Math.PI / 2} position={[10, 1, 0]} scale={[20, 1, 1]} /> */}
														{/* Accent (red) */}
														<Float speed={5} floatIntensity={2} rotationIntensity={2} visible={data.movingLights}>
															<Lightformer form="ring" color="white" intensity={1} scale={10} position={[-15, 4, -18]} target={[0, 0, 0]} />
														</Float>
														<Float speed={5} floatIntensity={2} rotationIntensity={2} visible={data.movingLights}>
															<Lightformer form="ring" color="white" intensity={1} scale={10} position={[15, 4, -18]} target={[0, 0, 0]} />
														</Float>
														{/* Background */}
														<mesh scale={100}>
															<sphereGeometry args={[1, 64, 64]} />
															<LayerMaterial side={THREE.BackSide}>
																<Base color="#444" alpha={1} mode="normal" />
																<Depth colorA="white" colorB="black" alpha={0.5} mode="normal" near={0} far={300} origin={[100, 100, 100]} />
															</LayerMaterial>
														</mesh>
													</Environment>

													<BakeShadows />
													<OrbitControls
														enablePan={false}
														enableZoom={false}
														minPolarAngle={Math.PI / 2.1}
														maxPolarAngle={Math.PI / 2.1}
														autoRotate={rotateModel}
														rotateSpeed={0.3}
													// makeDefault={true}

													/>
												</Suspense>
											</Canvas>
											<div className={"vt-nav-controllers"}>
												<div className="vt-arrow -left">
													<img src="/static/img/vt-arrow-left.png" alt="" className="" />
												</div>
												<div className="vt-arrow -right">
													<img src="/static/img/vt-arrow-right.png" alt="" className="" />
												</div>
											</div>
										</div>
										{colours &&
											<div className="productColours">
												<div className="productColoursHolder">
													{model.modelHigh &&
														ModelSwitch()
													}
													<div className="colours">
														{buttons()}
													</div>
													<div className="colourName  mt0px">
														<div className="title">
															{colours[activeIndex].title}
														</div>
														<div className="type">
															{colours[activeIndex].type}
														</div>
													</div>
												</div>
											</div>
										}
									</>
								}
							</>
							{/* } */}


						</div>
					)}
				</SiteContext.Consumer>
			}
		</>
	)
};

export default VirtualTour;